/**
 * Infrastructure API Client
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T014 - Create infrastructure API client
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Frontend HTTP client for infrastructure module endpoints
 * Uses centralized apiClient for MSAL token injection
 */

import { apiClient } from '../../../services/apiClient';
import {
  InfrastructureConfigDto,
  InfrastructureConfigUpdateDto,
  AciCapacityOverrideDto,
  KeyVaultReferenceDto,
  ConfigValidationResult,
  KeyVaultValidationResult,
  CollectedHealth,
  HealthHistoryResponse,
  CapacityType,
  DiscoveryResult,
  DiscoveredSubscription,
  DiscoveredResourceGroup,
} from '../types';

const API_BASE = '/api/infrastructure';

/**
 * Generic API wrapper using centralized apiClient with MSAL auth
 */
async function apiFetch<T>(endpoint: string, options?: { method?: string; body?: string }): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const config: { method?: string; data?: unknown } = {};
  if (options?.method) {
    config.method = options.method;
  }
  if (options?.body) {
    config.data = JSON.parse(options.body);
  }

  const response = await apiClient.request<T>({
    url,
    method: (config.method as 'GET' | 'POST' | 'PUT' | 'DELETE') || 'GET',
    data: config.data,
  });

  return response.data;
}

// =============================================================================
// Configuration API
// =============================================================================

/**
 * Get current infrastructure configuration
 * Returns null when infrastructure hasn't been configured yet (404)
 */
export async function getConfig(): Promise<InfrastructureConfigDto | null> {
  const response = await apiClient.request<{ configured: boolean; data: InfrastructureConfigDto | null } | InfrastructureConfigDto>({
    url: `${API_BASE}/config`,
    method: 'GET',
  });
  const body = response.data as any;
  if (body && 'configured' in body && !body.configured) return null;
  return response.data as InfrastructureConfigDto;
}

/**
 * Update infrastructure configuration
 */
export async function updateConfig(
  config: InfrastructureConfigUpdateDto
): Promise<InfrastructureConfigDto> {
  return apiFetch<InfrastructureConfigDto>('/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

/**
 * Validate configuration without saving
 */
export async function validateConfig(
  config: InfrastructureConfigUpdateDto
): Promise<ConfigValidationResult> {
  return apiFetch<ConfigValidationResult>('/config/validate', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

// =============================================================================
// Health API
// =============================================================================

/**
 * Get collected health status (Spec 216 T024/T025 — DB-only read model).
 * The old POST /health/check live-prober is DELETED (FR-018); the webapp
 * displays what the collection job measured.
 */
export async function getHealth(): Promise<CollectedHealth> {
  return apiFetch<CollectedHealth>('/health');
}

/**
 * Get health check history
 */
export async function getHealthHistory(params?: {
  component?: string;
  since?: string;
  limit?: number;
}): Promise<HealthHistoryResponse> {
  const searchParams = new URLSearchParams();
  if (params?.component) searchParams.set('component', params.component);
  if (params?.since) searchParams.set('since', params.since);
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const endpoint = queryString ? `/health/history?${queryString}` : '/health/history';

  return apiFetch<HealthHistoryResponse>(endpoint);
}

// =============================================================================
// Capacity Override API
// =============================================================================

/**
 * Get all capacity type overrides
 */
export async function getOverrides(): Promise<{ items: AciCapacityOverrideDto[] }> {
  return apiFetch<{ items: AciCapacityOverrideDto[] }>('/overrides');
}

/**
 * Create or update a capacity type override
 */
export async function upsertOverride(
  capacityType: CapacityType,
  override: Omit<AciCapacityOverrideDto, 'id' | 'capacityType'>
): Promise<AciCapacityOverrideDto> {
  return apiFetch<AciCapacityOverrideDto>(`/overrides/${encodeURIComponent(capacityType)}`, {
    method: 'PUT',
    body: JSON.stringify(override),
  });
}

/**
 * Delete a capacity type override
 */
export async function deleteOverride(capacityType: CapacityType): Promise<void> {
  await apiFetch<void>(`/overrides/${encodeURIComponent(capacityType)}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Key Vault Reference API
// =============================================================================

/**
 * Get all Key Vault references
 */
export async function getKeyVaultReferences(): Promise<{ items: KeyVaultReferenceDto[] }> {
  return apiFetch<{ items: KeyVaultReferenceDto[] }>('/keyvault/references');
}

/**
 * Create or update a Key Vault reference
 */
export async function upsertKeyVaultReference(
  purpose: string,
  reference: Omit<KeyVaultReferenceDto, 'id' | 'purpose' | 'validationStatus' | 'lastValidated'>
): Promise<KeyVaultReferenceDto> {
  return apiFetch<KeyVaultReferenceDto>(`/keyvault/references/${encodeURIComponent(purpose)}`, {
    method: 'PUT',
    body: JSON.stringify(reference),
  });
}

/**
 * Delete a Key Vault reference
 */
export async function deleteKeyVaultReference(purpose: string): Promise<void> {
  await apiFetch<void>(`/keyvault/references/${encodeURIComponent(purpose)}`, {
    method: 'DELETE',
  });
}

/**
 * Validate all Key Vault references
 */
export async function validateKeyVaultReferences(): Promise<KeyVaultValidationResult> {
  return apiFetch<KeyVaultValidationResult>('/keyvault/validate', {
    method: 'POST',
  });
}

// =============================================================================
// Discovery API
// =============================================================================

/**
 * List Azure subscriptions accessible to the current identity
 */
export async function listSubscriptions(): Promise<DiscoveredSubscription[]> {
  const result = await apiFetch<{ subscriptions: DiscoveredSubscription[] }>('/discover/subscriptions');
  return result.subscriptions;
}

/**
 * List resource groups within a subscription
 */
export async function listResourceGroups(subscriptionId: string): Promise<DiscoveredResourceGroup[]> {
  const result = await apiFetch<{ resourceGroups: DiscoveredResourceGroup[] }>(
    `/discover/resource-groups?subscriptionId=${encodeURIComponent(subscriptionId)}`
  );
  return result.resourceGroups;
}

/**
 * Discover existing Azure resources (ACRs, Key Vaults) for a subscription
 */
export async function discoverResources(
  subscriptionId: string,
  resourceGroup?: string
): Promise<DiscoveryResult> {
  const params = new URLSearchParams({ subscriptionId });
  if (resourceGroup?.trim()) params.set('resourceGroup', resourceGroup.trim());
  return apiFetch<DiscoveryResult>(`/discover?${params.toString()}`);
}

// =============================================================================
// Exports
// =============================================================================

export const infrastructureApi = {
  // Configuration
  getConfig,
  updateConfig,
  validateConfig,

  // Health
  getHealth,
  getHealthHistory,

  // Overrides
  getOverrides,
  upsertOverride,
  deleteOverride,

  // Key Vault
  getKeyVaultReferences,
  upsertKeyVaultReference,
  deleteKeyVaultReference,
  validateKeyVaultReferences,

  // Discovery
  listSubscriptions,
  listResourceGroups,
  discoverResources,
};

export default infrastructureApi;
